import {
	Alert,
	Button,
	Col,
	Divider,
	Flex,
	Form,
	Input,
	Modal,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Typography,
	Upload,
	UploadFile,
} from 'antd';
import { FileSearchOutlined, UploadOutlined } from '@ant-design/icons';
import { FC, useState, Fragment, useRef, useEffect } from 'react';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import formRule from '@/libs/antd/rules';
import { useModals } from '@/components/modals/ModalProvider';
import { FileData } from '@/libs/fleet-api/services/types';
import {
	useGetManualMPPSlik,
	useSubmitManualMPPSlikMutation,
	SubmitManualSlikPayload,
	SubmitManualSlikManagement,
	SubmitManualSlikShareholder,
} from '@/libs/fleet-api/services/mpp';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import { useApp } from '@/App';

// Approval Action Constants based on API enum (0-7)
const APPROVAL_ACTIONS = [
	{ value: 'APPROVE', label: 'Approve' },
	{ value: 'REJECT', label: 'Reject' },
] as const;

interface ManagementManualSlikItem {
	id?: number;
	shareholderId?: number;
	name?: string;
	slikResult?: number;
	idebFile?: FileData;
}

interface ShareholderManualSlikItem {
	id?: number;
	shareholderId?: number;
	name?: string;
	slikResult?: number;
	idebFile?: FileData;
}

export type ManualSLIKInputModalProps = {
	modal: {
		open: boolean;
		onCancel?: () => void;
	};
	close: () => void;
	data: {
		customerId: string;
		mppSlikInquiryId: number;
	};
	viewOnly?: boolean;
};

const ManualSLIKInputModal: FC<ManualSLIKInputModalProps> = ({
	modal,
	close,
	data,
	viewOnly = false,
}) => {
	const { openPreviewModal } = useModals();
	const { message } = useApp();
	const [showSuccessModal, setShowSuccessModal] = useState(false);

	// Fetch manual SLIK data
	const {
		data: manualSlikData,
		isFetching: isLoadingManualData,
		error: manualDataError,
	} = useGetManualMPPSlik({
		customerId: data.customerId,
	});

	const { form, fieldName } = useForm();

	// Extract data from API response
	const slikFile = manualSlikData?.data?.slikFile;
	const slikResult = manualSlikData?.data?.slikResult;
	const managementManualSliks = manualSlikData?.data?.managementManualSliks;
	const shareholderManualSliks = manualSlikData?.data?.shareholderManualSliks;

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const submitManualMutation = useSubmitManualMPPSlikMutation();

	// Prevent double submission - use ref to ensure it's never reset
	const hasSubmittedRef = useRef(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Reset submission state when modal opens
	useEffect(() => {
		if (modal.open) {
			hasSubmittedRef.current = false;
			setIsSubmitting(false);
		}
	}, [modal.open]);

	const handleSubmit = async (values: unknown) => {
		// Prevent double submission - check both state and ref
		if (
			isSubmitting ||
			submitManualMutation.isPending ||
			hasSubmittedRef.current
		) {
			console.warn('Double submission prevented');
			return;
		}

		setIsSubmitting(true);
		hasSubmittedRef.current = true;

		try {
			const formValues = values as Record<string, unknown>;

			// Validate required data
			if (!manualSlikData?.data) {
				message.error('Manual SLIK data is not loaded. Please try again.');
				setIsSubmitting(false);
				return;
			}

			const payload: SubmitManualSlikPayload = {
				id: formValues.id as number,
				slikResult: formValues.slikResult as number,
				slikFile: formValues.slikFile as FileData,
				managementManualSliks:
					formValues.managementManualSliks as SubmitManualSlikManagement[],
				shareholderManualSliks:
					formValues.shareholderManualSliks as SubmitManualSlikShareholder[],
				notes: formValues.notes as string,
				approvalAction: formValues.approvalAction as number,
			};

			console.log('Submitting manual SLIK payload:', {
				id: payload.id,
				hasFile: !!payload.slikFile,
				managementCount: payload.managementManualSliks?.length || 0,
				shareholderCount: payload.shareholderManualSliks?.length || 0,
				notes: payload.notes,
				approvalAction: payload.approvalAction,
			});

			const result = await submitManualMutation.mutateAsync(payload);
			console.log('Manual SLIK submission result:', result);

			setShowSuccessModal(true);
			// close();
		} catch (error) {
			console.error('Failed to submit manual SLIK:', error);
			message.error('Failed to submit manual SLIK. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleFileUpload = async (
		file: File,
		type: string,
		recordId?: number,
	) => {
		const fileid = Date.now().toString();
		const filepath = `customer/${data.customerId}/manual-slik/${type}${recordId ? `/${recordId}` : ''}`;

		try {
			message.open({
				type: 'loading',
				content: `Uploading ${type} file`,
				duration: 0,
				key: fileid,
			});

			const url = await uploadMutation.mutateAsync({
				file: file as unknown as File,
				fileid,
				filepath,
			});

			message.destroy(fileid);
			return {
				id: fileid,
				name: file.name,
				path: filepath,
				url,
				format: file.type,
			} as FileData;
		} catch {
			message.open({
				type: 'error',
				content: `Failed uploading ${type} file`,
			});
			return null;
		}
	};

	// Show loading state while fetching data
	if (isLoadingManualData) {
		return (
			<Modal
				{...modal}
				style={{ top: 20 }}
			>
				<Space
					direction="vertical"
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: 300,
						height: 'calc(100vh - 200px)',
						overflow: 'auto',
						maxWidth: '100%',
					}}
					size="large"
				>
					<Typography.Title level={4}>Loading...</Typography.Title>
					<Spin size="large" />
				</Space>
			</Modal>
		);
	}

	// Show error state if data fetching failed
	if (manualDataError) {
		return (
			<Modal
				{...modal}
				style={{ top: 20 }}
				onCancel={() => close()}
				cancelText="Close"
				cancelButtonProps={{ style: { display: 'none' } }}
				okButtonProps={{ style: { display: 'none' } }}
			>
				<Space
					direction="vertical"
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: 300,
						height: 'calc(100vh - 200px)',
						overflow: 'auto',
						maxWidth: '100%',
					}}
					size="large"
				>
					<Alert
						message="Error Loading"
						description="Failed to load manual SLIK data. Please try again."
						type="error"
						showIcon
					/>
					<Button onClick={() => close()}>Close</Button>
				</Space>
			</Modal>
		);
	}

	return (
		<Fragment>
			<Modal
				{...modal}
				style={{ top: 20 }}
				cancelButtonProps={{ style: { display: 'none' } }}
				okButtonProps={{ style: { display: 'none' } }}
				width={1200}
			>
				<Space
					direction="vertical"
					style={{
						display: 'flex',
						height: 'calc(100vh - 200px)',
						overflow: 'auto',
						maxWidth: '100%',
					}}
					size="large"
				>
					<FleetTitle>Manual SLIK Input</FleetTitle>

					<Form
						{...ANTD_DEFAULT_PROPS.FORM}
						form={form}
						onFinish={handleSubmit}
						initialValues={{
							id: data.mppSlikInquiryId,
							slikFile: slikFile
								? {
										id: String(slikFile.id),
										name: slikFile.name,
										path: slikFile.path,
										url: slikFile.url,
										format: slikFile.format,
									}
								: undefined,
							slikResult,
							managementManualSliks:
								managementManualSliks?.map(
									(item: ManagementManualSlikItem) => ({
										id: item.id,
										shareholderId: item.shareholderId,
										slikResult: item.slikResult,
										idebFile: item.idebFile
											? {
													id: String(item.idebFile.id),
													name: item.idebFile.name,
													path: item.idebFile.path,
													url: item.idebFile.url,
													format: item.idebFile.format,
												}
											: undefined,
									}),
								) || [],
							shareholderManualSliks:
								shareholderManualSliks?.map(
									(item: ShareholderManualSlikItem) => ({
										id: item.id,
										shareholderId: item.shareholderId,
										slikResult: item.slikResult,
										idebFile: item.idebFile
											? {
													id: String(item.idebFile.id),
													name: item.idebFile.name,
													path: item.idebFile.path,
													url: item.idebFile.url,
													format: item.idebFile.format,
												}
											: undefined,
									}),
								) || [],
							notes: '',
							approvalAction: 'APPROVE', // Default to 'Submitted'
						}}
					>
						<Space
							direction="vertical"
							style={{ display: 'flex' }}
							size="large"
						>
							<Typography.Title level={4}>SLIK Information</Typography.Title>

							{/* <Alert
								message="Manual SLIK Entry"
								description="Please upload the SLIK PDF file and enter the results manually."
								type="info"
								showIcon
							/> */}
							<Form.Item
								name={fieldName(['id'])}
								noStyle
							/>

							<Row gutter={[40, 20]}>
								<Col span={12}>
									<Form.Item
										label="Upload SLIK File"
										name={fieldName(['slikFile'])}
										rules={[formRule.required()]}
										getValueProps={(value) => {
											const file = value as FileData;
											return {
												fileList: file?.id
													? ([
															{
																uid: file.id,
																name: file.name,
																thumbUrl: file.url,
															},
														] as UploadFile[])
													: [],
											};
										}}
										normalize={({ file }: { file: UploadFile }) => {
											return {
												id: file.uid,
												format: file.type,
												name: file.name,
												url: file.thumbUrl,
											} as FileData;
										}}
									>
										<Upload.Dragger
											listType="picture"
											maxCount={1}
											beforeUpload={() => false}
											accept=".pdf,.doc,.docx"
											disabled={uploadMutation.isPending || viewOnly}
											onRemove={() => {
												form.setFieldValue(fieldName(['slikFile']), null);
												return false;
											}}
											onChange={async ({ file }) => {
												const uploadedFile = await handleFileUpload(
													file as unknown as File,
													'slik',
												);
												if (uploadedFile) {
													form.setFieldValue(
														fieldName(['slikFile']),
														uploadedFile,
													);
												}
											}}
										>
											<UploadOutlined
												style={{
													fontSize: 25,
													color: '#BABABA',
													display: 'block',
												}}
											/>
										</Upload.Dragger>
									</Form.Item>
								</Col>
								<Col span={12}>
									<Form.Item
										label="SLIK Result"
										name={fieldName(['slikResult'])}
										rules={[formRule.required()]}
									>
										<Select
											disabled={uploadMutation.isPending || viewOnly}
											allowClear
											placeholder="Select SLIK result"
											options={Array(5)
												.fill(() => null)
												.map((_, i) => {
													const value = i + 1;
													return {
														label: value.toString(),
														value,
													};
												})}
										/>
									</Form.Item>
								</Col>
							</Row>

							<Divider />

							<FleetTitle>Management SLIK Results</FleetTitle>
							<Table
								bordered
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={
									managementManualSliks?.map(
										(record: ManagementManualSlikItem, key: number) => ({
											...record,
											key,
										}),
									) ?? []
								}
								columns={[
									{
										title: 'View Ideb',
										width: 110,
										align: 'center',
										render: (_, record: ManagementManualSlikItem) => (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (record.idebFile) {
														openPreviewModal(record.idebFile);
													}
												}}
											>
												<FileSearchOutlined
													style={{
														color: record.idebFile ? undefined : '#bbb',
													}}
												/>
											</a>
										),
									},
									{
										title: 'Upload Ideb',
										width: 110,
										align: 'center',
										render: (_, record, index) => (
											<Form.Item
												name={fieldName([
													'managementManualSliks',
													index,
													'idebFile',
												])}
												rules={[formRule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => ({
													id: file.uid,
													format: file.type,
													name: file.name,
													url: file.thumbUrl,
												})}
												initialValue={record.idebFile}
											>
												<Upload.Dragger
													listType="picture"
													accept=".pdf,.doc,.docx"
													maxCount={1}
													beforeUpload={() => false}
													disabled={uploadMutation.isPending || viewOnly}
													onRemove={() => {
														form.setFieldValue(
															fieldName([
																'managementManualSliks',
																index,
																'idebFile',
															]),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const uploadedFile = await handleFileUpload(
															file as unknown as File,
															'management-ideb',
															record.shareholderId,
														);
														if (uploadedFile) {
															form.setFieldValue(
																fieldName([
																	'managementManualSliks',
																	index,
																	'idebFile',
																]),
																uploadedFile,
															);
														}
													}}
												>
													<UploadOutlined
														style={{
															fontSize: 12,
															display: 'block',
														}}
													/>
												</Upload.Dragger>
											</Form.Item>
										),
									},
									{
										title: 'Nama Pengurus',
										width: 300,
										render: (_, record: ManagementManualSlikItem, index) => (
											<Fragment>
												<Form.Item
													noStyle
													name={fieldName([
														'managementManualSliks',
														index,
														'id',
													])}
												/>
												<Form.Item
													noStyle
													name={fieldName([
														'managementManualSliks',
														index,
														'shareholderId',
													])}
												/>
												<span>{record.name ?? '-'}</span>
											</Fragment>
										),
									},
									{
										title: 'SLIK Result',
										render: (_, _record, index) => (
											<Form.Item
												name={fieldName([
													'managementManualSliks',
													index,
													'slikResult',
												])}
												rules={[formRule.required()]}
											>
												<Select
													allowClear
													size="small"
													style={{ width: 80 }}
													disabled={uploadMutation.isPending || viewOnly}
													placeholder="Result"
													options={Array(5)
														.fill(() => null)
														.map((_, i) => {
															const value = i + 1;
															return {
																label: value.toString(),
																value,
															};
														})}
												/>
											</Form.Item>
										),
									},
								]}
							/>

							<FleetTitle>Shareholder SLIK Results</FleetTitle>
							<Table
								bordered
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={
									shareholderManualSliks?.map(
										(record: ShareholderManualSlikItem, key: number) => ({
											...record,
											key,
										}),
									) ?? []
								}
								columns={[
									{
										title: 'View Ideb',
										width: 110,
										align: 'center',
										render: (_, record: ShareholderManualSlikItem) => (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (record.idebFile) {
														openPreviewModal(record.idebFile);
													}
												}}
											>
												<FileSearchOutlined
													style={{
														color: record.idebFile ? undefined : '#bbb',
													}}
												/>
											</a>
										),
									},
									{
										title: 'Upload Ideb',
										width: 110,
										align: 'center',
										render: (_, record, index) => (
											<Form.Item
												name={fieldName([
													'shareholderManualSliks',
													index,
													'idebFile',
												])}
												rules={[formRule.required()]}
												getValueProps={(value) => {
													const file = value as FileData;
													return {
														fileList: file?.id
															? ([
																	{
																		uid: file.id,
																		name: file.name,
																		thumbUrl: file.url,
																	},
																] as UploadFile[])
															: [],
													};
												}}
												normalize={({ file }: { file: UploadFile }) => ({
													id: file.uid,
													format: file.type,
													name: file.name,
													url: file.thumbUrl,
												})}
												initialValue={record.idebFile}
											>
												<Upload.Dragger
													listType="picture"
													accept=".pdf,.doc,.docx"
													maxCount={1}
													beforeUpload={() => false}
													disabled={uploadMutation.isPending || viewOnly}
													onRemove={() => {
														form.setFieldValue(
															fieldName([
																'shareholderManualSliks',
																index,
																'idebFile',
															]),
															null,
														);
														return false;
													}}
													onChange={async ({ file }) => {
														const uploadedFile = await handleFileUpload(
															file as unknown as File,
															'shareholder-ideb',
															record.shareholderId,
														);
														if (uploadedFile) {
															form.setFieldValue(
																fieldName([
																	'shareholderManualSliks',
																	index,
																	'idebFile',
																]),
																uploadedFile,
															);
														}
													}}
												>
													<UploadOutlined
														style={{
															fontSize: 12,
															display: 'block',
														}}
													/>
												</Upload.Dragger>
											</Form.Item>
										),
									},
									{
										title: 'Nama Pemegang Saham',
										width: 300,
										render: (_, record: ShareholderManualSlikItem, index) => (
											<Fragment>
												<Form.Item
													noStyle
													name={fieldName([
														'shareholderManualSliks',
														index,
														'id',
													])}
												/>
												<Form.Item
													noStyle
													name={fieldName([
														'shareholderManualSliks',
														index,
														'shareholderId',
													])}
												/>
												<span>{record.name ?? '-'}</span>
											</Fragment>
										),
									},
									{
										title: 'SLIK Result',
										render: (_, _record, index) => (
											<Form.Item
												name={fieldName([
													'shareholderManualSliks',
													index,
													'slikResult',
												])}
												rules={[formRule.required()]}
											>
												<Select
													disabled={uploadMutation.isPending || viewOnly}
													allowClear
													size="small"
													style={{ width: 80 }}
													placeholder="Result"
													options={Array(5)
														.fill(() => null)
														.map((_, i) => {
															const value = i + 1;
															return {
																label: value.toString(),
																value,
															};
														})}
												/>
											</Form.Item>
										),
									},
								]}
							/>

							<Divider />

							{/* Approval and Notes Section */}
							<Row gutter={[40, 20]}>
								<Col span={12}>
									<Form.Item
										label="Approval"
										labelCol={{ span: 6 }}
										labelAlign="left"
										name={fieldName(['approvalAction'])}
										rules={[formRule.required()]}
									>
										<Select
											allowClear
											placeholder="Select approval action"
											options={APPROVAL_ACTIONS.map((action) => ({
												label: action.label,
												value: action.value,
											}))}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Row gutter={[40, 20]}>
								<Col span={12}>
									<Form.Item
										label="Catatan"
										labelCol={{ span: 6 }}
										labelAlign="left"
										name={fieldName(['notes'])}
										rules={[formRule.required()]}
									>
										<Input.TextArea
											rows={3}
											placeholder="Notes"
										/>
									</Form.Item>
								</Col>
							</Row>

							<Flex
								justify="space-between"
								style={{ margin: '20px 0' }}
							>
								<Button
									type="default"
									onClick={() => close()}
									style={{
										width: 120,
									}}
									disabled={
										isSubmitting ||
										uploadMutation.isPending ||
										submitManualMutation.isPending
									}
								>
									CANCEL
								</Button>
								<Button
									type="primary"
									htmlType="submit"
									loading={
										isSubmitting ||
										uploadMutation.isPending ||
										submitManualMutation.isPending
									}
									disabled={
										isSubmitting ||
										uploadMutation.isPending ||
										submitManualMutation.isPending ||
										isLoadingManualData
									}
									style={{
										width: 200,
									}}
								>
									SUBMIT
								</Button>
							</Flex>
						</Space>
					</Form>
				</Space>
			</Modal>

			{/* Success Modal */}
			<Modal
				open={showSuccessModal}
				title="Success"
				onCancel={() => {
					setShowSuccessModal(false);
					close();
				}}
				onOk={() => {
					setShowSuccessModal(false);
					close();
				}}
				okText="OK"
				cancelText="Close"
			>
				<Alert
					message="Submit SLIK Manual berhasil"
					description="Data SLIK manual telah berhasil dikirim untuk approval."
					type="success"
					showIcon
				/>
			</Modal>
		</Fragment>
	);
};

export default ManualSLIKInputModal;
